import { BodyText } from '@/components/BodyText';
import { Ellipsis } from '@/components/Ellipsis';
import { FileUploadStatus, FileWithStatus } from '@/types/upload';
import { formatFileSize } from '@/utils/format';
import { ERROR_MESSAGES, isInvalidFileTypeError, SUPPORTED_FILE_TYPES_MESSAGE } from '@/utils/validation';
import { Box, LinearProgress, ListItem, ListItemIcon, ListItemText, Tooltip, styled } from '@mui/material';
import { FileStatusIcon, FileStatusText } from '@/components/Upload/components/UploadComponents';
import { UploadTestIds } from '@/enums/uploadTestIds.enum';

const SecondaryText = styled('span')(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.875rem',
}));

const ErrorContainer = styled('span')(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
}));

const FlexContainer = styled('span')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
}));

const getUploadProgress = (file: FileWithStatus, uploadProgress: Record<string, number>) => {
  if (file.status.state !== FileUploadStatus.UPLOADING) {
    return;
  }

  const progress = uploadProgress[file.path] || 0;
  const isDeterminate = progress > 0;

  return (
    <>
      <LinearProgress
        variant={'determinate'}
        value={progress}
        sx={{ width: '38px' }}
        data-testid={`${UploadTestIds.UploadRowProgressBar}-${file.path}`}
      />
      {isDeterminate && (
        <SecondaryText data-testid={`${UploadTestIds.UploadRowProgress}-${file.path}`}>{`${progress}%`}</SecondaryText>
      )}
    </>
  );
};

const getSecondaryContent = (file: FileWithStatus, uploadProgress: Record<string, number>) => {
  if (file.status.state === FileUploadStatus.FAILED) {
    return (
      <ErrorContainer>
        <span data-testid={`${UploadTestIds.UploadRowErrorMessage}-${file.path}`}>
          {isInvalidFileTypeError(file.status.message) ? (
            <Tooltip title={SUPPORTED_FILE_TYPES_MESSAGE}>
              <Box
                component='span'
                sx={{
                  color: 'error.main',
                  fontSize: '0.875rem',
                  cursor: 'text',
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {file.status.message}
              </Box>
            </Tooltip>
          ) : (
            <Ellipsis
              text={file.status.message ?? ERROR_MESSAGES.UPLOAD_FAILED}
              sx={{
                color: file.status.code === FileUploadStatus.FILENAME_MISMATCH ? 'warning.dark' : 'error.main',
                fontSize: '0.875rem',
              }}
            />
          )}
        </span>
        {file.status.traceId && (
          <span data-testid={`${UploadTestIds.UploadRowTraceId}-${file.path}`}>
            <Ellipsis
              text={`Error code: ${file.status.traceId}`}
              sx={{ color: 'text.secondary', fontSize: '0.7rem' }}
            />
          </span>
        )}
      </ErrorContainer>
    );
  }

  return (
    <FlexContainer>
      <SecondaryText>{formatFileSize(file.size)} </SecondaryText>
      {file.status.state === FileUploadStatus.UPLOADING ? (
        getUploadProgress(file, uploadProgress)
      ) : (
        <span data-testid={`${UploadTestIds.UploadRowStatusText}-${file.path}`}>
          <FileStatusText file={file} />
        </span>
      )}
    </FlexContainer>
  );
};

export const UploadFileListItem = ({
  file,
  uploadProgress,
  index,
}: {
  file: FileWithStatus;
  uploadProgress: Record<string, number>;
  index: number;
}) => {
  return (
    <ListItem
      key={`${file.path}-${index}`}
      data-testid={`${UploadTestIds.UploadRow}-${file.path}`}
      sx={{ padding: theme => theme.spacing(1, 2), borderBottom: theme => `1px solid ${theme.palette.divider}` }}
    >
      <ListItemIcon sx={{ minWidth: '32px' }}>
        <FileStatusIcon file={file} />
      </ListItemIcon>
      <ListItemText
        primary={
          <BodyText overflow='hidden'>
            <Ellipsis text={file.name} />
          </BodyText>
        }
        secondary={getSecondaryContent(file, uploadProgress)}
      />
    </ListItem>
  );
};
